import { IonButton, IonButtons, IonContent, IonFooter, IonHeader, IonIcon, IonModal, IonTitle, IonToolbar } from "@ionic/react";
import { closeOutline } from "ionicons/icons";
import { Controller, SubmitHandler, useForm } from "react-hook-form";
import { ToolfinderFilterForm } from "./FormTypes";
import { FormControl, InputLabel, Select, MenuItem, InputAdornment, TextField, FormHelperText } from "@mui/material";
import { useEffect, useMemo } from "react";
import { getl2MaxMinValue } from "./FormHelpers";
import { useTranslation } from "react-i18next";

interface FilterModalProps {
    isOpen: boolean,
    setIsOpen: (val: boolean) => void,
    filterValues: ToolfinderFilterForm,
    submitCallback: (data: ToolfinderFilterForm) => void,
    strategy: string | undefined,
    ap: string | number | undefined,
}

const maxZAmount = 10;
const ttypes = ["n", "ns", "w", "ws", "wr", "wx", "h", "hr", "fhr"]


const ToolfinderFilterModal: React.FC<FilterModalProps> = ({ isOpen, setIsOpen, filterValues, submitCallback, strategy, ap }: FilterModalProps) => {
    const { control, reset, handleSubmit, formState: { errors }, watch } = useForm<ToolfinderFilterForm>({
        mode: "onTouched",
        reValidateMode: "onChange",
        defaultValues: useMemo(() => {
            return filterValues
        }, [filterValues])
    })
    const { t } = useTranslation();

    const selectedL2min = watch("l2min");
    const selectedL2max = watch("l2max");

    useEffect(() => {
        reset(filterValues);
    }, [filterValues, reset]);

    const showZMenuItems = () => {
        let rows = [];
        for (let i = 1; i <= maxZAmount; i++) {
            if (i !== 8) {
                rows.push(
                    <MenuItem
                        key={i}
                        value={i.toString()}
                    >{i}</MenuItem>
                )
            }
        }
        return rows;
    }

    const onSubmit: SubmitHandler<ToolfinderFilterForm> = data => {
        submitCallback(data)
    }


    return (
        <IonModal isOpen={isOpen} onDidDismiss={() => setIsOpen(false)} focusTrap={false}>
            <IonHeader>
                <IonToolbar>
                    <IonTitle>{t("toolfinder.filterModal.title")}</IonTitle>
                    <IonButtons slot="end">
                        <IonButton onClick={() => setIsOpen(false)}>
                            <IonIcon slot="icon-only" icon={closeOutline} />
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonHeader>
            <IonContent className="ion-padding">
                <form>
                    <div>
                        {t("toolfinder.filterModal.Schneidenlänge")} (l<sub>2</sub>)
                        <div style={{ display: "flex", gap: 10 }}>
                            <div>
                                <Controller
                                    control={control}
                                    name="l2min"
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <TextField
                                            variant="standard"
                                            label={t("toolfinder.filterModal.min")}
                                            value={value ?? ""}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            type="number"
                                            slotProps={{
                                                input: {
                                                    endAdornment: <InputAdornment position="end">mm</InputAdornment>,
                                                }
                                            }}
                                        />
                                    )}
                                    rules={{
                                        max: { value: selectedL2max !== "" ? Number(selectedL2max) : 100, message: t("toolfinder.filterModal.valueTooBig") },
                                        min: { value: ap ? Number(ap) : 0.1, message: t("toolfinder.filterModal.valueTooSmall") }
                                    }}
                                />
                                {/* @ts-ignore */}
                                {errors.l2min && <div className="error">{errors.l2min.message}</div>}
                            </div>
                            <div>
                                <Controller
                                    control={control}
                                    name="l2max"
                                    render={({ field: { value, onChange, onBlur } }) => (
                                        <TextField
                                            variant="standard"
                                            label={t("toolfinder.filterModal.max")}
                                            value={value ?? ""}
                                            onChange={onChange}
                                            onBlur={onBlur}
                                            type="number"
                                            InputProps={{
                                                endAdornment: <InputAdornment position="start">mm</InputAdornment>,
                                            }}
                                        />
                                    )}
                                    rules={{
                                        max: { value: 100, message: t("toolfinder.filterModal.valueTooBig") },
                                        min: { value: ap ? getl2MaxMinValue(ap.toString(), selectedL2min) : 0.1, message: t("toolfinder.filterModal.valueTooSmall") }
                                    }}
                                />
                                {/* @ts-ignore */}
                                {errors.l2max && <div className="error">{errors.l2max.message}</div>}

                            </div>
                        </div>
                        <FormHelperText>{t("toolfinder.filterModal.Hinweis")}: a<sub>p</sub> &le; l<sub>2</sub>min &le; l<sub>2</sub>max &le; 100 mm</FormHelperText>
                    </div>
                    {/* Anzahl Schneiden */}
                    <div style={{ marginTop: 20 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="z">{t("toolfinder.filterModal.Anzahl Schneiden")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="z"
                                    >
                                        <MenuItem
                                            value={""}
                                        ><em>{t("toolfinder.filterModal.Alle")}</em></MenuItem>
                                        {showZMenuItems()}

                                    </Select>
                                </FormControl>
                            )}
                            control={control}
                            name="z"
                        />
                    </div>

                    {/* Verzahnungs-Typ */}
                    <div style={{ marginTop: 10 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="ttype">{t("toolfinder.filterModal.Verzahnungs-Typ")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="ttype"
                                    >
                                        <MenuItem
                                            value={""}
                                        ><em>{t("toolfinder.filterModal.Alle")}</em></MenuItem>
                                        {ttypes.map(ttype => <MenuItem key={ttype} value={ttype}>{ttype.toUpperCase()}</MenuItem>)}

                                    </Select>
                                </FormControl>
                            )}
                            control={control}
                            name="ttype"
                        />
                    </div>

                    {/* Schneidkante */}
                    <div style={{ marginTop: 10 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="cuttingEdge">{t("toolfinder.filterModal.Schneidkante")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="cuttingEdge"
                                    >
                                        <MenuItem value={""}>
                                            <em>{t("toolfinder.filterModal.Alle")}</em>
                                        </MenuItem>

                                        <MenuItem value={"fase"}>{t("toolfinder.filterModal.Fase")}</MenuItem>
                                        <MenuItem value={"radius"}>{t("toolfinder.filterModal.Radius")}</MenuItem>
                                        <MenuItem value={"scharfkantig"}>{t("toolfinder.filterModal.Scharfkantig")}</MenuItem>

                                    </Select>
                                    {/* {strategy && strategy === "std-ko" && <FormHelperText>Für die Strategie Kopieren eignen sich nur Radius- und Torus-Fräser.</FormHelperText>} */}
                                </FormControl>
                            )}
                            control={control}
                            name="cuttingEdge"
                        />
                    </div>


                    {/* Beschichtung */}
                    <div style={{ marginTop: 10 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="coating">{t("toolfinder.filterModal.Beschichtung")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="coating"
                                    >
                                        <MenuItem value={""}>
                                            <em>{t("toolfinder.filterModal.Alle")}</em>
                                        </MenuItem>

                                        <MenuItem value={"coated"}>{t("toolfinder.filterModal.Beschichtet")}</MenuItem>
                                        <MenuItem value={"notCoated"}>{t("toolfinder.filterModal.Unbeschichtet")}</MenuItem>

                                    </Select>
                                </FormControl>
                            )}
                            control={control}
                            name="coating"
                        />
                    </div>

                    {/* Schaft */}
                    <div style={{ marginTop: 10 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="weldon">{t("toolfinder.filterModal.Schaft")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="weldon"
                                    >
                                        <MenuItem value={""}>
                                            <em>{t("toolfinder.filterModal.Alle")}</em>
                                        </MenuItem>

                                        <MenuItem value={"weldon"}>{t("toolfinder.filterModal.Weldon")}</MenuItem>
                                        <MenuItem value={"smooth"}>{t("toolfinder.filterModal.Glatt")}</MenuItem>

                                    </Select>
                                </FormControl>
                            )}
                            control={control}
                            name="weldon"
                        />
                    </div>

                    {/* Innenkühlung */}
                    <div style={{ marginTop: 10 }}>
                        <Controller
                            render={({ field: { onChange, onBlur, value } }) => (
                                <FormControl variant="standard" sx={{ minWidth: "100%", maxWidth: "100%" }}>
                                    <InputLabel id="ik">{t("toolfinder.filterModal.Innenkühlung")}</InputLabel>
                                    <Select
                                        onChange={onChange}
                                        onBlur={onBlur}
                                        value={value ?? ""}
                                        variant="standard"
                                        labelId="ik"
                                    >
                                        <MenuItem value={""}>
                                            <em>{t("toolfinder.filterModal.Alle")}</em>
                                        </MenuItem>

                                        <MenuItem value={"withIk"}>{t("toolfinder.filterModal.mit Innenkühlung")}</MenuItem>
                                        <MenuItem value={"withoutIk"}>{t("toolfinder.filterModal.ohne Innenkühlung")}</MenuItem>

                                    </Select>
                                </FormControl>
                            )}
                            control={control}
                            name="ik"
                        />
                    </div>
                </form>

            </IonContent>
            <IonFooter>
                <IonToolbar>
                    <IonButtons slot="end">
                        <IonButton onClick={() => setIsOpen(false)}>
                            {t("Abbrechen")}
                        </IonButton>
                        <IonButton onClick={handleSubmit(onSubmit)} fill="solid" color="primary">
                            {t("toolfinder.filterModal.Anwenden")}
                        </IonButton>
                    </IonButtons>
                </IonToolbar>
            </IonFooter>
        </IonModal>
    )
}

export default ToolfinderFilterModal;